<template>
  <SmSkeleton v-if="props.loading" height="26px" width="80px" />
  <div
    v-else
    :class="{
      'rounded-sm p-1 px-2': appearance === 'default',
      'bg-gray-100 dark:bg-gray-700': appearance === 'default' && !color,
    }"
    :style="tagStyle">
    <SmTooltip :active="!hideTooltip">
      <template #content>
        <slot name="tooltip">
          <span v-if="props.tooltip">{{ props.tooltip }}</span>
          <span v-else-if="props.label">{{ props.label }}</span>
        </slot>
      </template>

      <slot name="label">
        <div class="flex items-center gap-2">
          <component
            :is="props.icon"
            v-if="props.icon && appearance === 'default'"
            :size="16" />

          <TextMultiline
            :text="props.label || i18n.t('unknown')"
            :lines-to-show="1"
            :show-tool-tip="true" />
          <slot name="action"></slot>
        </div>
      </slot>
    </SmTooltip>
  </div>
</template>

<script setup lang="ts">
  import { computed, type PropType } from 'vue'
  import { Component } from 'lucide-vue-next'
  import { useI18n } from 'vue-i18n'
  const i18n = useI18n()

  const props = defineProps({
    label: {
      type: String,
      default: '',
    },
    icon: {
      type: Object as PropType<Component>,
      default: undefined,
    },
    appearance: {
      type: String as PropType<'default' | 'text'>,
      default: 'default',
    },
    hideTooltip: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: undefined,
    },
  })

  function hexToRgba(color: string, alpha: number): string | null {
    const normalized = color.replace('#', '')
    if (![3, 6].includes(normalized.length)) return null

    const full = normalized.length === 3
      ? normalized
          .split('')
          .map((char) => char + char)
          .join('')
      : normalized

    const intVal = Number.parseInt(full, 16)
    const r = (intVal >> 16) & 255
    const g = (intVal >> 8) & 255
    const b = intVal & 255
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  const tagStyle = computed(() => {
    if (!props.color || props.appearance !== 'default') return undefined

    const background = hexToRgba(props.color, 0.16) ?? props.color
    return {
      backgroundColor: background,
      color: props.color,
    }
  })
</script>
